/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { t as translate } from '../../platform/i18n/index.js';
import { loadVendorScript } from '../../bootstrap.js';
import { enterGuest, enterTeacherGuest } from '../onboarding/onboarding.runtime.js';
import { syncAndroidAccessToken } from './android-uikit.runtime.js';
import { setAuthStage } from '../session/session.runtime.js';
import { applyChronicleInitials, syncKuniQuick } from '../shell/navigation.runtime.js';

export var $ = (selector, root = document) => root.querySelector(selector);

export var $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

export var API = '/api/v1';

export var t = translate;

export var DEFAULT_NAVIGATION_ORDER = Object.freeze(['today','study','knowledge','campus','more']);

export var DEFAULT_ACCENT_COLOR = '#1668E3';

export var DEFAULT_INTERFACE_MODE = 'supergrok';

export var DEFAULT_INTERFACE_STYLE = 'default';

export var DEFAULT_THEME = 'dark';

export var ONBOARDING_VERSION = 14;

var normalizeInviteCode = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return code.length >= 6 && code.length <= 64 && /^[A-Z0-9-]+$/.test(code) ? code : '';
};

export var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Canonical path for each app view (pretty SPA URLs). */
var VIEW_PATHS = Object.freeze({
  home: '/main',
  studyhub: '/study',
  morehub: '/more',
  schedule: '/schedule',
  skip: '/skip',
  homework: '/homework',
  requirements: '/requirements',
  semester: '/semester',
  courses: '/courses',
  course: '/course',
  lesson: '/lesson',
  materials: '/materials',
  attendance: '/attendance',
  wiki: '/wiki',
  teachers: '/teachers',
  notes: '/notes',
  solutions: '/solutions',
  navigator: '/navigator',
  events: '/events',
  council: '/student-council',
  notifications: '/notifications',
  settings: '/settings',
  group: '/group',
  admin: '/admin',
  ui_text_editor: '/edit',
});

/** Path segment aliases → view id */
var VIEW_ALIASES = Object.freeze({
  main: 'home',
  home: 'home',
  study: 'studyhub',
  studyhub: 'studyhub',
  more: 'morehub',
  morehub: 'morehub',
  schedule: 'schedule',
  skip: 'skip',
  homework: 'homework',
  requirements: 'requirements',
  semester: 'semester',
  courses: 'courses',
  course: 'course',
  lesson: 'lesson',
  materials: 'materials',
  attendance: 'attendance',
  wiki: 'wiki',
  teachers: 'teachers',
  notes: 'notes',
  solutions: 'solutions',
  navigator: 'navigator',
  events: 'events',
  council: 'council',
  'student-council': 'council',
  notifications: 'notifications',
  settings: 'settings',
  group: 'group',
  admin: 'admin',
  edit: 'ui_text_editor',
});

export var __routerSilent = false;

export function setRouterSilent(value) { __routerSilent = Boolean(value); }

function pathForView(view, extra = {}) {
  if (view === 'course' && (extra.id || state.courseSubjectID)) {
    return '/course/' + encodeURIComponent(extra.id || state.courseSubjectID);
  }
  if (view === 'lesson' && (extra.id || state.contextEntryID)) {
    return '/lesson/' + encodeURIComponent(extra.id || state.contextEntryID);
  }
  return VIEW_PATHS[view] || ('/' + encodeURIComponent(view || 'schedule'));
}

export function syncViewURL(view, { replace = false } = {}) {
  if (__routerSilent) return;
  const path = pathForView(view);
  const current = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
  const target = path.replace(/\/+$/, '') || '/';
  if (current === target) {
    // Keep history state in sync even if path matches.
    try { history.replaceState({ view, kuni: 1 }, '', path); } catch {}
    return;
  }
  try {
    if (replace) history.replaceState({ view, kuni: 1 }, '', path);
    else history.pushState({ view, kuni: 1 }, '', path);
  } catch {}
  state.deepLink = parseDeepLink();
}

export function parseDeepLink() {
  let path = window.location.pathname || '/';
  try { path = decodeURIComponent(path); } catch { return { type: 'none' }; }
  path = path.replace(/\/+$/, '') || '/';
  const parts = path.split('/').filter(Boolean);
  const query = new URLSearchParams(window.location.search);
  if (parts[0] === 'join') return { type: 'join', code: normalizeInviteCode(parts[1]) };
  if (parts[0] === 'lesson' && UUID_RE.test(parts[1] || '')) return { type: 'lesson', id: parts[1] };
  if (parts[0] === 'note' && UUID_RE.test(parts[1] || '')) return { type: 'note', id: parts[1] };
  if (parts[0] === 'homework' && UUID_RE.test(parts[1] || '')) return { type: 'homework', id: parts[1] };
  if (parts[0] === 'requirement' && UUID_RE.test(parts[1] || '')) return { type: 'requirement', id: parts[1] };
  if (parts[0] === 'material' && UUID_RE.test(parts[1] || '')) return { type: 'material', id: parts[1] };
  if (parts[0] === 'course' && UUID_RE.test(parts[1] || '')) return { type: 'course', id: parts[1] };
  if (parts[0] === 'auth' && parts[1] === 'reset-password' && parts[2]) return { type: 'reset-password', token: parts.slice(2).join('/') };
  if (parts.length === 1 && parts[0] === 'privacy') return { type: 'legal', document: 'privacy' };
  if (parts.length === 1 && parts[0] === 'consent') return { type: 'legal', document: 'consent' };
  if (parts.length === 1 && parts[0] === 'edit') return { type: 'view', view: 'ui_text_editor' };
  if (parts.length === 1 && VIEW_ALIASES[parts[0]]) return { type: 'view', view: VIEW_ALIASES[parts[0]] };
  // Bare /course or /lesson without id → parent list views
  if (parts.length === 1 && parts[0] === 'course') return { type: 'view', view: 'courses' };
  if (parts.length === 1 && parts[0] === 'lesson') return { type: 'view', view: 'schedule' };
  // Backward-compatible parser for links created by releases before 10.1.
  const join = normalizeInviteCode(query.get('join'));
  if (join) return { type: 'join', code: join };
  for (const [key, type] of [['entry', 'lesson'], ['note', 'note'], ['homework', 'homework'], ['requirement', 'requirement'], ['material', 'material']]) {
    const id = query.get(key); if (id && UUID_RE.test(id)) return { type, id };
  }
  const view = query.get('view');
  const subject = query.get('subject');
  if (view === 'course' && subject && UUID_RE.test(subject)) return { type: 'course', id: subject };
  if (view && VIEW_ALIASES[view]) return { type: 'view', view: VIEW_ALIASES[view] };
  if (view && Object.prototype.hasOwnProperty.call(VIEW_PATHS, view)) return { type: 'view', view };
  return { type: 'none' };
}

var initialDeepLink = parseDeepLink();

export var state = {
  accessToken: '', user: null, isGuest: false, roles: [], memberships: [], groups: [], groupID: '', publicTeacher: null,
  view: 'schedule', refreshPromise: null, weekOffset: 0, scheduleFocusEntryID: '',
  interfacePreferences: { navigation_order: [...DEFAULT_NAVIGATION_ORDER], accent_color: DEFAULT_ACCENT_COLOR, interface_mode: DEFAULT_INTERFACE_MODE, interface_style: DEFAULT_INTERFACE_STYLE, theme: DEFAULT_THEME, chronicle_initials: false },
  catalogs: { subjects: [], teachers: [], rooms: [] }, subgroups: [],
  pendingInviteCode: initialDeepLink.type === 'join' ? initialDeepLink.code : '', invitePreview: null,
  deepLink: initialDeepLink,
  wikiSearch: '', wikiCategory: '', teachersScope: 'group', teachersSearch: '', teachersMode: 'directory', teacherSelectedID: '', teacherSelectedName: '', teacherSelectedSubjectID: '', notesSearch: '', notesSubjectID: '', notesScheduleID: '', noteMaxBytes: 25*1024*1024,
  eventSearch: '', eventCategory: '', eventPeriod: 'upcoming', councilTab: 'mine', councilStatus: '', navigatorSearch: '', navigatorCategory: '', courseSubjectID: '', contextEntryID: '',
  adminTab: 'users', adminSelectedGroupID: '', adminSearch: '', adminCatalogType: 'subject', adminCatalogSearch: '', adminOrgSelection: '', aiAdminPolicyBundle: null, parserUniversityID: '',
  legalDocuments: null, legalStatus: null,
};

export async function loadLegalDocuments() {
  if (state.legalDocuments) return state.legalDocuments;
  const response = await fetch('/legal-documents.ru.json', { cache:'no-store' });
  if (!response.ok) throw new Error('Не удалось загрузить юридические документы');
  state.legalDocuments = await response.json();
  return state.legalDocuments;
}

function legalTemplate(value, extra = {}) {
  const vars = {
    operator_name: t('legal.operator.name'),
    operator_address: t('legal.operator.address'),
    operator_email: t('legal.operator.email'),
    ...extra,
  };
  return String(value || '').replace(/\{\{([a-z0-9_]+)\}\}/gi, (_, key) => vars[key] ?? `{{${key}}}`);
}

function legalDocumentHTML(document) {
  if (!document) return '<div class="empty">Документ не найден.</div>';
  const meta = t('legal.page.version', { version: document.version || '—', date: document.effective_at || '—' });
  return `<header class="legal-document-head"><span class="eyebrow">kUni · юридические документы</span><h1>${escapeHTML(document.title || '')}</h1><p>${escapeHTML(legalTemplate(document.lead || '', {privacy_version:document.privacy_version || ''}))}</p><small>${escapeHTML(meta)}</small></header><div class="legal-document-body">${(document.sections||[]).map((section)=>`<section><h2>${escapeHTML(section.title||'')}</h2>${(section.paragraphs||[]).map((paragraph)=>`<p>${escapeHTML(legalTemplate(paragraph,{privacy_version:document.privacy_version||''}))}</p>`).join('')}${section.list?.length?`<ul>${section.list.map((item)=>`<li>${escapeHTML(legalTemplate(item,{privacy_version:document.privacy_version||''}))}</li>`).join('')}</ul>`:''}</section>`).join('')}</div>`;
}

export async function showLegalPage(kind) {
  const docs = await loadLegalDocuments();
  const doc = docs?.[kind];
  $('#auth-view').hidden = true;
  $('#app-view').hidden = true;
  if (typeof syncKuniQuick === 'function') syncKuniQuick();
  $('#legal-view').hidden = false;
  $('#legal-document').innerHTML = legalDocumentHTML(doc);
  document.title = `${doc?.title || 'Документы'} · kUni`;
  if (typeof applyChronicleInitials === 'function') applyChronicleInitials();
}

export function hideLegalPage() {
  $('#legal-view').hidden = true;
  document.title = 'kUni';
}

export var titleKeys = {
  home: ['view.home.title', 'view.home.subtitle'],
  studyhub: ['view.studyhub.title', 'view.studyhub.subtitle'],
  morehub: ['view.morehub.title', 'view.morehub.subtitle'],
  schedule: ['view.schedule.title', 'view.schedule.subtitle'],
  skip: ['view.skip.title', 'view.skip.subtitle'],
  homework: ['view.homework.title', 'view.homework.subtitle'],
  requirements: ['view.requirements.title', 'view.requirements.subtitle'],
  semester: ['view.semester.title', 'view.semester.subtitle'],
  courses: ['view.courses.title', 'view.courses.subtitle'],
  course: ['view.course.title', 'view.course.subtitle'],
  lesson: ['view.lesson.title', 'view.lesson.subtitle'],
  materials: ['view.materials.title', 'view.materials.subtitle'],
  attendance: ['view.attendance.title', 'view.attendance.subtitle'],
  wiki: ['view.wiki.title', 'view.wiki.subtitle'],
  teachers: ['view.teachers.title', 'view.teachers.subtitle'],
  notes: ['view.notes.title', 'view.notes.subtitle'],
  solutions: ['view.solutions.title', 'view.solutions.subtitle'],
  navigator: ['view.navigator.title', 'view.navigator.subtitle'],
  events: ['view.events.title', 'view.events.subtitle'],
  council: ['view.council.title', 'view.council.subtitle'],
  notifications: ['view.notifications.title', 'view.notifications.subtitle'],
  settings: ['view.settings.title', 'view.settings.subtitle'],
  group: ['view.group.title', 'view.group.subtitle'],
  admin: ['view.admin.title', 'view.admin.subtitle'],
  ui_text_editor: ['view.ui_text_editor.title', 'view.ui_text_editor.subtitle'],
};

export var titleFor = (view) => {
  const [titleKey, subtitleKey] = titleKeys[view] || titleKeys.schedule;
  return [t(titleKey), t(subtitleKey)];
};

export var SIMPLE_STUDY_VIEWS = Object.freeze([
  ['semester','section.semester'],['courses','section.courses'],['homework','section.homework'],['requirements','section.requirements'],['solutions','section.solutions'],['notes','section.notes'],['materials','section.materials'],['attendance','section.attendance'],['skip','section.skip'],
]);

export var SIMPLE_MORE_VIEWS = Object.freeze([
  ['teachers','section.teachers'],['wiki','section.wiki'],['events','section.events'],['council','section.council'],['notifications','section.notifications'],['group','section.group'],['navigator','section.navigator'],['admin','section.admin'],
]);

export var sectionDefs = {
  today: { labelKey: 'section.today', views: [['schedule','section.schedule'],['skip','section.skip']] },
  study: { labelKey: 'section.study', views: [['semester','section.semester'],['courses','section.courses'],['homework','section.homework'],['requirements','section.requirements'],['attendance','section.attendance']] },
  knowledge: { labelKey: 'section.knowledge', views: [['materials','section.materials'],['solutions','section.solutions'],['notes','section.notes'],['teachers','section.teachers'],['wiki','section.wiki']] },
  campus: { labelKey: 'section.campus', views: [['navigator','section.navigator'],['events','section.events'],['council','section.council']] },
  more: { labelKey: 'section.more', views: [['notifications','section.notifications'],['group','section.group'],['admin','section.admin']] },
};

export function sectionForView(view) {
  if (view === 'course') return 'study';
  return Object.entries(sectionDefs).find(([,section])=>section.views.some(([id])=>id===view))?.[0] || 'today';
}

export function availableSectionViews(sectionID) {
  const guestViews = new Set(['schedule','navigator']);
  return (sectionDefs[sectionID]?.views || []).filter(([view]) => (
    (!state.isGuest || guestViews.has(view)) && (view !== 'admin' || isAdmin())
  ));
}

export var simpleMode = () => !state.isGuest && state.interfacePreferences.interface_mode === 'simple';

export // Guest browsing intentionally uses the v10 context-first shell. Guest permissions are still
// enforced separately; this only selects the presentation layer.
var contextMode = () => state.interfacePreferences.interface_mode === 'context';

export var superGrokMode = () => !state.isGuest && state.interfacePreferences.interface_mode === 'supergrok';

export var compactMode = () => simpleMode() || contextMode() || superGrokMode();

export function simpleParentView(view) {
  if (view === 'home' || view === 'schedule' || view === 'studyhub' || view === 'morehub') return view;
  if (view === 'lesson') return 'schedule';
  if (view === 'course') return 'studyhub';
  if (SIMPLE_STUDY_VIEWS.some(([id]) => id === view)) return 'studyhub';
  return 'morehub';
}

export function availableSimpleViews(parent) {
  if (parent === 'studyhub') return SIMPLE_STUDY_VIEWS;
  if (parent !== 'morehub') return [];
  return SIMPLE_MORE_VIEWS.filter(([view]) => view !== 'admin' || isAdmin());
}

export var escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
})[ch]);

export var fmtDate = (value, options = {}) => value ? new Intl.DateTimeFormat('ru-RU', options).format(new Date(value)) : '—';

export var fmtTime = (value) => fmtDate(value, { hour: '2-digit', minute: '2-digit' });

export var fmtDay = (value) => fmtDate(value, { weekday: 'long', day: 'numeric', month: 'long' });

export var fmtDateTime = (value) => fmtDate(value, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export var isoLocal = (date) => {
  const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};

export var cookie = (name) => document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${name}=`))?.slice(name.length + 1) || '';

var toastTimer;

function placeToastNode(node) {
  const dialog = $('#dialog');
  const host = dialog?.open ? dialog : document.body;
  if (node.parentElement !== host) host.append(node);
}

export function toast(message, type = 'info') {
  const node = $('#toast');
  if (!node) return;
  const kind = type === 'ok' || type === 'success' ? 'ok'
    : type === 'warn' || type === 'warning' ? 'warn'
      : type === 'err' || type === 'error' ? 'err'
        : 'info';
  placeToastNode(node);
  node.textContent = message;
  node.hidden = false;
  node.className = `toast toast-${kind}`;
  node.setAttribute('role', 'status');
  node.setAttribute('aria-live', 'polite');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    node.hidden = true;
    node.className = 'toast';
    if (node.parentElement !== document.body) document.body.append(node);
  }, kind === 'err' ? 4200 : 3200);
}

export function setAuthError(message = '') {
  const node = $('#auth-error');
  node.textContent = message;
  node.hidden = !message;
}

export function isAppBootActive() {
  return document.documentElement.classList.contains('kuni-app-boot')
    || document.documentElement.dataset.kuniAppBoot === 'session'
    || document.documentElement.dataset.kuniAppBoot === 'join'
    || isJoinBootActive();
}

function isJoinBootActive() {
  return document.documentElement.dataset.kuniJoinBoot === '1'
    || document.documentElement.classList.contains('kuni-join-boot')
    || document.documentElement.dataset.kuniAppBoot === 'join';
}

export function setSessionHint(active) {
  try {
    if (active) localStorage.setItem('kuni-session-hint', '1');
    else localStorage.removeItem('kuni-session-hint');
  } catch { /* ignore */ }
}

function configureBootOverlayCopy(mode = 'session') {
  const title = $('#kuni-boot-title');
  const text = $('#kuni-boot-text');
  if (mode === 'join') {
    if (title) title.textContent = 'Открываем группу…';
    if (text) text.textContent = 'Сейчас покажем расписание — без логина.';
  } else {
    if (title) title.textContent = 'Открываем kUni…';
    if (text) text.textContent = 'Подтягиваем сессию — без экрана входа.';
  }
}

export /** Full-screen overlay — never paint #auth-view while we expect an app session or auto-join. */
function setAppBootSplash(mode = 'session') {
  const kind = mode === 'join' ? 'join' : 'session';
  document.documentElement.dataset.kuniAppBoot = kind;
  document.documentElement.classList.add('kuni-app-boot');
  if (kind === 'join') {
    document.documentElement.dataset.kuniJoinBoot = '1';
    document.documentElement.classList.add('kuni-join-boot');
  }
  configureBootOverlayCopy(kind);
  const auth = $('#auth-view');
  if (auth) auth.hidden = true;
  const overlay = $('#kuni-boot-overlay');
  if (overlay) overlay.setAttribute('aria-busy', 'true');
  // Overlay stays hidden until the 800ms timer in boot.js (or a late re-arm).
  if (!window.__kuniBootTimer && !document.documentElement.classList.contains('kuni-boot-visible')) {
    window.__kuniBootTimer = setTimeout(() => {
      window.__kuniBootTimer = 0;
      if (!isAppBootActive()) return;
      document.documentElement.classList.add('kuni-boot-visible');
    }, 800);
  }
}

function setJoinBootSplash(show) {
  if (show) setAppBootSplash('join');
  else clearAppBoot();
}

export /**
 * End boot overlay. Call ONLY after #auth-view is already hidden (app path)
 * or intentionally shown (auth path) so login never paints for a frame.
 */
function clearAppBoot() {
  if (window.__kuniBootTimer) {
    clearTimeout(window.__kuniBootTimer);
    window.__kuniBootTimer = 0;
  }
  document.documentElement.dataset.kuniAppBoot = '';
  document.documentElement.dataset.kuniJoinBoot = '0';
  document.documentElement.classList.remove('kuni-app-boot', 'kuni-join-boot', 'kuni-boot-visible');
  const overlay = $('#kuni-boot-overlay');
  if (overlay) {
    overlay.hidden = true;
    overlay.setAttribute('aria-busy', 'false');
  }
}

function clearJoinBoot() { clearAppBoot(); }

export /** Show app chrome, keep auth dead, then drop overlay — zero auth paint. */
function revealAppFromBoot() {
  const auth = $('#auth-view');
  const app = $('#app-view');
  if (auth) auth.hidden = true;
  if (app) app.hidden = false;
  document.documentElement.classList.remove('kuni-auth-ready');
  clearAppBoot();
}

function revealAppFromJoin() { revealAppFromBoot(); }

export /** Drop overlay and show auth (invite or welcome). */
function revealAuthFromBoot(stage = 'welcome') {
  const auth = $('#auth-view');
  const app = $('#app-view');
  if (app) app.hidden = true;
  document.documentElement.classList.add('kuni-auth-ready');
  if (auth) auth.hidden = false;
  clearAppBoot();
  setAuthStage(stage);
}

export /** Failed auto-open: drop overlay and show invite CTAs (not welcome/login). */
function revealInviteAuthFromJoin() {
  revealAuthFromBoot('invite');
}

export function syncInviteBanner() {
  const banner = $('#auth-invite');
  if (!banner) return;
  const active = Boolean(state.pendingInviteCode);
  banner.hidden = !active;
  const discovery = $('#auth-group-discovery');
  if (discovery && $('.auth-box')?.dataset.authStage === 'guest') discovery.hidden = active;
  else if (discovery && active) discovery.hidden = true;
  const guestBlock = $('#auth-guest-block');
  if (guestBlock) guestBlock.hidden = true;
  if (active && !state.invitePreview) {
    $('#auth-invite-group').textContent = 'Проверяем ссылку…';
    $('#auth-invite-context').textContent = 'Открываем группу — расписание появится сразу.';
    const mode = $('#auth-invite-mode');
    mode.textContent = '…'; mode.className = 'auth-invite-mode';
  }
  // During app/join boot keep auth fully hidden — never force stages onto screen.
  if (isAppBootActive()) {
    const auth = $('#auth-view');
    if (auth) auth.hidden = true;
    syncInviteStageCopy();
    return;
  }
  // Invite deep-link on visible auth: dedicated onboarding, never bare login/welcome.
  if (active && $('#auth-view') && !$('#auth-view').hidden) {
    const stage = $('.auth-box')?.dataset.authStage;
    if (!stage || stage === 'welcome' || stage === 'guest') setAuthStage('invite');
  }
  syncInviteStageCopy();
}

export function syncInviteStageCopy() {
  const preview = state.invitePreview;
  const groupName = preview?.group?.name || $('#auth-invite-group')?.textContent || 'группу';
  const lead = $('#auth-invite-lead');
  if (lead) {
    lead.textContent = preview?.approval_required
      ? `Группа «${groupName}»: зарегистрируйтесь, чтобы отправить заявку. Или сначала посмотрите расписание как гость.`
      : `Группа «${groupName}»: зарегистрируйтесь, чтобы вступить и открыть задания, автомат и материалы. Можно сначала посмотреть расписание.`;
  }
  const loginTitle = $('#auth-login-title');
  const loginLead = $('#auth-login-lead');
  if (state.pendingInviteCode) {
    if (loginTitle) loginTitle.textContent = 'Войти и вступить';
    if (loginLead) loginLead.textContent = `После входа приглашение в «${groupName}» применится автоматически.`;
  } else {
    if (loginTitle) loginTitle.textContent = 'С возвращением';
    if (loginLead) loginLead.textContent = 'Войдите, чтобы открыть группу и задания.';
  }
  const regTitle = $('#auth-register-title');
  const regLead = $('#auth-register-lead');
  if (state.pendingInviteCode) {
    if (regTitle) regTitle.textContent = 'Аккаунт и вступление';
    if (regLead) regLead.textContent = `Создайте аккаунт — и kUni сразу применит приглашение в «${groupName}».`;
  } else {
    if (regTitle) regTitle.textContent = 'Создать аккаунт';
    if (regLead) regLead.textContent = 'Минута — и вы внутри. Пароль от 10 символов.';
  }
}

export function authBackStage() {
  return state.pendingInviteCode ? 'invite' : 'welcome';
}

export async function enterInviteGuest({ quiet = false } = {}) {
  const group = state.invitePreview?.group;
  if (!group?.id) {
    setAuthError('Сначала дождитесь проверки ссылки-приглашения.');
    revealInviteAuthFromJoin();
    return;
  }
  try {
    await enterGuest(group);
    if (!quiet) toast(`Группа «${group.name}» открыта. Зарегистрируйтесь, чтобы вступить.`);
  } catch (error) {
    revealInviteAuthFromJoin();
    throw error;
  }
}

export function clearPendingInvite() {
  state.pendingInviteCode = '';
  state.invitePreview = null;
  state.deepLink = { type:'none' };
  const url = new URL(window.location.href);
  url.searchParams.delete('join');
  history.replaceState(null, '', `/${url.search}${url.hash}`);
  syncInviteBanner();
}

export async function loadInvitePreview() {
  const code = state.pendingInviteCode;
  if (!code) return null;
  syncInviteBanner();
  try {
    const preview = await api(`/public/invites/${encodeURIComponent(code)}`, {}, false);
    const group = await api(`/public/groups/${encodeURIComponent(preview.group_id)}`, {}, false);
    if (code !== state.pendingInviteCode) return null;
    state.invitePreview = { ...preview, group };
    $('#auth-invite-group').textContent = group?.name || 'Учебная группа';
    $('#auth-invite-context').textContent = groupDiscoveryContext(group);
    const mode = $('#auth-invite-mode');
    mode.textContent = preview.approval_required ? 'с одобрением' : 'доступ сразу';
    mode.className = `auth-invite-mode ${preview.approval_required ? 'approval' : 'instant'}`;
    syncInviteStageCopy();
    // Never surface auth while app/join boot is covering the screen.
    if (!isAppBootActive() && $('#auth-view') && !$('#auth-view').hidden) {
      const stage = $('.auth-box')?.dataset.authStage;
      if (!stage || stage === 'welcome' || stage === 'guest' || stage === 'login') setAuthStage('invite');
    }
    return state.invitePreview;
  } catch (error) {
    if (code !== state.pendingInviteCode) return null;
    state.pendingInviteCode = ''; state.invitePreview = null; state.deepLink = {type:'none'};
    const url = new URL(window.location.href); url.searchParams.delete('join'); history.replaceState(null,'',`/${url.search}${url.hash}`);
    const message = error?.message || 'Приглашение недействительно или уже истекло.';
    // While boot overlay is up, boot decides what to show — never unhide auth mid-flight.
    if (isAppBootActive()) {
      state.__inviteBootError = message;
      return null;
    }
    setAuthStage('welcome');
    syncInviteBanner();
    setAuthError(message);
    return null;
  }
}

function canonicalDeepLink(type, id) {
  const path = ({lesson:'lesson',note:'note',homework:'homework',requirement:'requirement',material:'material',course:'course'})[type];
  return path && id ? new URL(`/${path}/${encodeURIComponent(id)}`, window.location.origin).toString() : new URL('/', window.location.origin).toString();
}

function inviteDeepLink(code) {
  return new URL(`/join/${encodeURIComponent(normalizeInviteCode(code))}`, window.location.origin).toString();
}

export /** Prefer server-provided Telegram deep link; fall back to web /join. */
function inviteShareLinks(result = {}) {
  const code = normalizeInviteCode(result.code || result.Code || '');
  const web = String(result.web_deep_link || result.webDeepLink || '').trim() || (code ? inviteDeepLink(code) : '');
  const telegram = String(result.telegram_deep_link || result.telegramDeepLink || '').trim();
  return { web, telegram, primary: telegram || web };
}

function groupDiscoveryContext(group) {
  if (!group) return 'Учебная группа';
  const program = [group.program_code, group.program_name].filter(Boolean).join(' · ');
  return [group.university_name, group.institute_name, program].filter(Boolean).join(' · ');
}

function groupDiscoveryCourse(group) {
  const course = Number(group?.current_course || 0);
  return course > 0 ? `${course} курс` : '';
}

var authGroupResultCache = new Map();

var authGroupSearchSerial = 0;

var authGroupSearchTimer = 0;

function renderAuthGroupSearchState(html = '') {
  const root = $('#auth-group-search-results');
  if (!root) return;
  root.hidden = !html;
  root.innerHTML = html;
}

function renderAuthGroupSearchResults(items, query) {
  const root = $('#auth-group-search-results');
  if (!root) return;
  authGroupResultCache.clear();
  const normalized = String(query || '').trim().toLocaleLowerCase('ru');
  const sorted = [...(items || [])].sort((a,b) => {
    const ax = String(a.name || '').toLocaleLowerCase('ru') === normalized ? 0 : 1;
    const bx = String(b.name || '').toLocaleLowerCase('ru') === normalized ? 0 : 1;
    if (ax !== bx) return ax - bx;
    return `${a.university_name||''} ${a.name||''}`.localeCompare(`${b.university_name||''} ${b.name||''}`,'ru');
  });
  if (!sorted.length) {
    renderAuthGroupSearchState('<div class="auth-group-search-state"><strong>Группа не нашлась</strong>Проверь название или добавь к запросу ВУЗ.</div>');
    return;
  }
  sorted.forEach((group) => authGroupResultCache.set(String(group.id), group));
  root.hidden = false;
  root.innerHTML = sorted.map((group) => {
    const course = groupDiscoveryCourse(group);
    return `<button class="auth-group-result" type="button" data-auth-group-id="${group.id}"><span class="auth-group-result-copy"><span class="auth-group-result-title"><strong>${escapeHTML(group.name||'Группа')}</strong>${course?`<span>${escapeHTML(course)}</span>`:''}</span><span>${escapeHTML(groupDiscoveryContext(group))}</span></span><b aria-hidden="true">→</b></button>`;
  }).join('');
  $$('[data-auth-group-id]', root).forEach((button) => button.addEventListener('click', () => {
    const group = authGroupResultCache.get(button.dataset.authGroupId);
    if (group) enterGuest(group).catch((error)=>setAuthError(error.message));
  }));
}

async function searchPublicGroups(query) {
  const q = String(query || '').trim();
  const serial = ++authGroupSearchSerial;
  if (q.length < 2) {
    renderAuthGroupSearchState(q ? '<div class="auth-group-search-state">Введи хотя бы 2 символа.</div>' : '');
    return;
  }
  renderAuthGroupSearchState('<div class="auth-group-search-state"><strong>Ищу группу…</strong>Проверяем каталог kUni.</div>');
  try {
    const page = await api(`/public/groups?search=${encodeURIComponent(q)}&limit=30`, {}, false);
    if (serial !== authGroupSearchSerial) return;
    renderAuthGroupSearchResults(page?.items || page || [], q);
  } catch (error) {
    if (serial !== authGroupSearchSerial) return;
    renderAuthGroupSearchState(`<div class="auth-group-search-state"><strong>Поиск пока недоступен</strong>${escapeHTML(error.message||'Попробуй ещё раз.')}</div>`);
  }
}

export function initAuthGroupDiscovery() {
  const input = $('#auth-group-search-input');
  if (!input || input.dataset.bound === 'true') return;
  input.dataset.bound = 'true';
  input.addEventListener('input', () => {
    clearTimeout(authGroupSearchTimer);
    authGroupSearchSerial += 1;
    const q = input.value;
    renderAuthGroupSearchState(q.trim().length >= 2 ? '<div class="auth-group-search-state">Ищу…</div>' : '');
    authGroupSearchTimer = setTimeout(() => searchPublicGroups(q), 170);
  });
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const first = $('#auth-group-search-results [data-auth-group-id]');
    if (!first) return;
    event.preventDefault(); first.click();
  });
}

var authTeacherResultCache = new Map();

var authTeacherSearchSerial = 0;

var authTeacherSearchTimer = 0;

function renderAuthTeacherSearchState(html = '') {
  const root = $('#auth-teacher-search-results'); if (!root) return;
  root.hidden = !html; root.innerHTML = html;
}

function renderAuthTeacherSearchResults(items) {
  const root = $('#auth-teacher-search-results'); if (!root) return;
  authTeacherResultCache.clear();
  if (!(items||[]).length) { renderAuthTeacherSearchState('<div class="auth-group-search-state"><strong>Преподаватель не нашёлся</strong>Проверь ФИО. В каталоге появляются преподаватели из импортированного расписания.</div>'); return; }
  (items||[]).forEach((item)=>authTeacherResultCache.set(String(item.id),item));
  root.hidden=false;
  root.innerHTML=(items||[]).map((item)=>`<button class="auth-group-result" type="button" data-auth-teacher-id="${item.id}"><span class="auth-group-result-copy"><span class="auth-group-result-title"><strong>${escapeHTML(item.full_name||item.short_name||'Преподаватель')}</strong></span><span>${escapeHTML([item.university_short_name,item.university_name].filter(Boolean).join(' · '))}</span></span><b aria-hidden="true">→</b></button>`).join('');
  $$('[data-auth-teacher-id]',root).forEach((button)=>button.addEventListener('click',()=>{const teacher=authTeacherResultCache.get(button.dataset.authTeacherId);if(teacher)enterTeacherGuest(teacher).catch((error)=>setAuthError(error.message));}));
}

async function searchPublicTeachers(query) {
  const q=String(query||'').trim(); const serial=++authTeacherSearchSerial;
  if(q.length<2){renderAuthTeacherSearchState(q?'<div class="auth-group-search-state">Введи хотя бы 2 символа.</div>':'');return;}
  renderAuthTeacherSearchState('<div class="auth-group-search-state"><strong>Ищу преподавателя…</strong>Проверяем каталог расписания.</div>');
  try{const items=await api(`/public/teachers?search=${encodeURIComponent(q)}&limit=20`,{},false);if(serial!==authTeacherSearchSerial)return;renderAuthTeacherSearchResults(items||[]);}catch(error){if(serial!==authTeacherSearchSerial)return;renderAuthTeacherSearchState(`<div class="auth-group-search-state"><strong>Поиск пока недоступен</strong>${escapeHTML(error.message||'Попробуй ещё раз.')}</div>`);}
}

export function initAuthTeacherDiscovery(){
  const input=$('#auth-teacher-search-input'); if(!input||input.dataset.bound==='true')return; input.dataset.bound='true';
  input.addEventListener('input',()=>{clearTimeout(authTeacherSearchTimer);authTeacherSearchSerial+=1;const q=input.value;renderAuthTeacherSearchState(q.trim().length>=2?'<div class="auth-group-search-state">Ищу…</div>':'');authTeacherSearchTimer=setTimeout(()=>searchPublicTeachers(q),170);});
  input.addEventListener('keydown',(event)=>{if(event.key!=='Enter')return;const first=$('#auth-teacher-search-results [data-auth-teacher-id]');if(!first)return;event.preventDefault();first.click();});
}

export async function copyText(value, successMessage = 'Скопировано') {
  const text = String(value || '');
  if (!text) throw new Error('Нечего копировать');
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
  } else {
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    if (!copied) throw new Error('Не удалось скопировать');
  }
  toast(successMessage);
}

function normalizeQRCodeElement(element) {
  if (!element) return false;

  // qrcode.js' canvas renderer creates both a <canvas> and a fallback <img>.
  // Firefox may make that fallback image visible after makeImage(), which used
  // to render two identical QR codes. Keep exactly one rendered QR node.
  var canvases = [...element.querySelectorAll(':scope > canvas')];
  if (canvases.length) {
    canvases.slice(1).forEach((node) => node.remove());
    element.querySelectorAll(':scope > img').forEach((node) => node.remove());
    element.querySelectorAll(':scope > table').forEach((node) => node.remove());
    return true;
  }

  var images = [...element.querySelectorAll(':scope > img')];
  if (images.length) {
    images.slice(1).forEach((node) => node.remove());
    element.querySelectorAll(':scope > table').forEach((node) => node.remove());
    return true;
  }

  var tables = [...element.querySelectorAll(':scope > table')];
  if (tables.length) {
    tables.slice(1).forEach((node) => node.remove());
    return true;
  }

  return false;
}

export function renderQRCode(target, value, { size = 220 } = {}) {
  const element = typeof target === 'string' ? $(target) : target;
  if (!element || !value) return false;
  const qrValue = String(value);

  if (typeof window.QRCode === 'undefined') {
    element.innerHTML = '<div class="qr-loading">Готовим QR…</div>';
    delete element.dataset.qrValue;
    loadVendorScript('/vendor/qrcode.min.js')
      .then(() => renderQRCode(element, qrValue, { size }))
      .catch((error) => {
        console.error('QR vendor failed to load', error);
        element.innerHTML = '<div class="qr-error">QR-генератор не загрузился</div>';
      });
    return false;
  }

  // Idempotent re-renders still normalize the existing DOM, so an older
  // qrcode.js canvas+image pair is repaired when the dialog is reopened.
  if (element.dataset.qrValue === qrValue && normalizeQRCodeElement(element)) {
    return true;
  }

  element.replaceChildren();
  delete element.dataset.qrValue;
  try {
    new window.QRCode(element, {
      text: qrValue,
      width: size,
      height: size,
      colorDark: '#000000',
      colorLight: '#ffffff',
      correctLevel: window.QRCode.CorrectLevel.M,
    });
    normalizeQRCodeElement(element);
    element.dataset.qrValue = qrValue;
    return true;
  } catch (error) {
    console.error('QR generation failed', error);
    element.innerHTML = '<div class="qr-error">Не удалось построить QR</div>';
    delete element.dataset.qrValue;
    return false;
  }
}

export function downloadQRCode(target, filename = 'kuni-qr.png') {
  const element = typeof target === 'string' ? $(target) : target;
  if (!element) throw new Error('QR-код не найден');
  const canvas = element.querySelector('canvas');
  const image = element.querySelector('img');
  const href = canvas?.toDataURL?.('image/png') || image?.src;
  if (!href) throw new Error('QR-код ещё не готов');
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
}

export function replaceLocationPath(path='/') { history.replaceState(null, '', path); state.deepLink = parseDeepLink(); }

export async function rawRequest(path, options = {}) {
  const headers = new Headers(options.headers || {});
  if (state.accessToken) headers.set('Authorization', `Bearer ${state.accessToken}`);
  if (options.body && !headers.has('Content-Type') && !(options.body instanceof FormData) && !(options.body instanceof Blob)) headers.set('Content-Type', 'application/json');
  return fetch(`${API}${path}`, { ...options, headers, credentials: 'same-origin' });
}

export async function parseBody(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return null; }
}

export async function refreshSession() {
  if (state.refreshPromise) return state.refreshPromise;
  state.refreshPromise = (async () => {
    let csrf = '';
    try { csrf = decodeURIComponent(cookie('kuni_csrf')); } catch { return false; }
    if (!csrf) return false;
    const response = await rawRequest('/auth/refresh', { method: 'POST', headers: { 'X-CSRF-Token': csrf }, body: '{}' });
    const body = await parseBody(response);
    if (!response.ok) return false;
    state.accessToken = body.data.access_token;
    state.user = body.data.user;
    syncAndroidAccessToken();
    return true;
  })().finally(() => { state.refreshPromise = null; });
  return state.refreshPromise;
}

export async function api(path, options = {}, retry = true) {
  let response = await rawRequest(path, options);
  if (response.status === 401 && retry && !path.startsWith('/auth/')) {
    if (await refreshSession()) response = await rawRequest(path, options);
  }
  const body = await parseBody(response);
  if (!response.ok) {
    const details = body?.error?.details && typeof body.error.details === 'object' ? body.error.details : {};
    let message = body?.error?.message || `HTTP ${response.status}`;
    const code = body?.error?.code || 'request_failed';
    // Permanent-delete failures must be actionable from the UI. Preserve the
    // structured backend details and include the blocking FK/table in the toast
    // instead of collapsing everything to “связанная запись”.
    if (code === 'delete_blocked') {
      const location = [details.table, details.column].filter(Boolean).join('.');
      const blocker = [location, details.constraint].filter(Boolean).join(' / ');
      if (blocker) message += `: ${blocker}`;
      else if (details.sqlstate) message += ` (SQLSTATE ${details.sqlstate})`;
    }
    const error = new Error(message);
    error.code = code;
    error.status = response.status;
    error.details = details;
    error.requestID = body?.error?.request_id || response.headers.get('X-Request-ID');
    throw error;
  }
  return body?.data ?? null;
}


// The organization API is cursor-paginated (100 groups per page). MIIT has
// more than 100 groups, so callers that build the schedule/group selector must
// walk every page instead of silently exposing only the oldest 100 rows.
export async function loadAllGroupPages(path = '/groups', retry = true) {
  const [base, rawQuery = ''] = String(path || '/groups').split('?', 2);
  const items = [];
  let cursor = '';
  for (let page = 0; page < 200; page += 1) {
    const params = new URLSearchParams(rawQuery);
    params.set('limit', '100');
    if (cursor) params.set('cursor', cursor); else params.delete('cursor');
    const result = await api(`${base}?${params.toString()}`, {}, retry);
    items.push(...(result?.items || result || []));
    cursor = String(result?.next_cursor || '');
    if (!cursor) return items;
  }
  throw new Error('Слишком много страниц каталога групп');
}

export function trackProduct(eventName, properties = {}) {
  if (!state.accessToken || state.isGuest) return;
  const source = /KUniAndroid\//.test(navigator.userAgent) ? 'android' : 'web';
  rawRequest('/analytics/events', { method:'POST', body:JSON.stringify({ event_name:eventName, source, properties }) }).catch(() => {});
}

export function currentGroup() { return state.groups.find((group) => group.id === state.groupID) || null; }

function roleCovers(item, scopeType, scopeID) {
  if (!item?.scope || !scopeType) return false;
  if (item.scope.type === 'global') return true;
  if (item.scope.type === scopeType && item.scope.id === scopeID) return true;
  const group = currentGroup();
  if (!group) return false;
  if (scopeType === 'group' && scopeID === group.id) {
    if (item.scope.type === 'program' && item.scope.id === group.academic_program_id) return true;
    if (item.scope.type === 'institute' && item.scope.id === group.institute_id) return true;
    if (item.scope.type === 'university' && item.scope.id === group.university_id) return true;
  }
  if (scopeType === 'institute' && scopeID === group.institute_id && item.scope.type === 'university' && item.scope.id === group.university_id) return true;
  return false;
}

export function hasRole(role, groupID = state.groupID) {
  return state.roles.some((item) => item.role === role && roleCovers(item, 'group', groupID));
}

export function hasScopedRole(role, scopeType, scopeID) { return state.roles.some((item) => item.role === role && roleCovers(item, scopeType, scopeID)); }

var hasGlobalRole = (role) => state.roles.some((item) => item.role === role && item.scope.type === 'global');

export var isProjectAdmin = () => hasGlobalRole('project_admin');

export var isAdmin = () => state.roles.some((item) => ['project_admin','university_admin','institute_admin','group_admin'].includes(item.role));

export var adminHasRole = (role) => state.roles.some((item)=>item.role===role);

export var canAdminCatalog = () => isProjectAdmin() || adminHasRole('university_admin') || adminHasRole('institute_admin');

export var canManageParsers = () => isProjectAdmin() || adminHasRole('university_admin');

export function initCoreRuntime() {
  $('#legal-back')?.addEventListener('click', () => { window.location.assign('/main'); });
}
